import { APP_VERSION } from '@/version';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Monitor, Calendar, Youtube, Cpu, Clock, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

function PublicHeader() {
  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center space-x-4">
            <img
              src="/curling-stadium-logo.png"
              alt="Curling Stadium Manager"
              className="h-8 w-8 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-bold text-xl">Curling Stadium Manager</span>
            <Badge variant="secondary">v{APP_VERSION}</Badge>
          </div>
          <nav className="flex items-center space-x-4">
            <Button variant="ghost" asChild>
              <Link to="/">Home</Link>
            </Button>
            <Button asChild>
              <Link to="/login">Sign In</Link>
            </Button>
          </nav>
        </div>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-muted-foreground">
          <p>&copy; 2026 Curling Stadium Manager. Built for the curling broadcast community.</p>
        </div>
      </div>
    </footer>
  );
}

export default function AboutStreamingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      <PublicHeader />

      {/* Hero */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-6">
            <Cpu className="h-4 w-4" />
            Automated Streaming
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
            Your streams start themselves.<br />
            <span className="text-primary">No one has to press Record.</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            CSM can automatically start and stop OBS at exactly the right time, on the right sheet,
            and rename the YouTube broadcast so viewers know what they're watching — all without
            anyone sitting at the computer.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-2">How It Works</h2>
          <p className="text-muted-foreground text-lg mb-10">
            There are two pieces: the <strong className="text-foreground">CSM server</strong> (in the cloud)
            and the <strong className="text-foreground">CSM agent</strong> (a small program that runs on each
            facility's broadcast computer).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2">
                  <Monitor className="h-6 w-6 text-blue-500" />
                </div>
                <CardTitle>The CSM Server</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-2">
                <p>
                  This is the central brain. Administrators use it to set up which games are being
                  streamed, on which sheets, and at what times.
                </p>
                <p>
                  It stores the schedule, holds the YouTube credentials securely, and waits for
                  the on-site agent to check in.
                </p>
              </CardContent>
            </Card>

            <Card className="border-green-200 dark:border-green-800">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-2">
                  <Cpu className="h-6 w-6 text-green-500" />
                </div>
                <CardTitle>The On-Site Agent</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-2">
                <p>
                  A lightweight program that runs quietly in the background on the facility's
                  broadcast computer — the same machine that runs OBS.
                </p>
                <p>
                  It checks in with the server every minute, receives the day's schedule, and
                  handles everything from there automatically.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Step-by-step flow */}
          <h3 className="text-xl font-semibold mb-6">What happens on game day</h3>
          <div className="space-y-4 mb-12">
            {[
              {
                icon: Calendar,
                color: 'text-blue-500',
                bg: 'bg-blue-500/10',
                step: '1',
                title: 'Club staff enters the schedule',
                desc: 'Before the event, club staff log into CSM and enter game times, sheet assignments, and team names. Schedules can be entered manually, imported from a CSV file, or synced automatically from Google Sheets or CurlingClubManager.',
              },
              {
                icon: Wifi,
                color: 'text-green-500',
                bg: 'bg-green-500/10',
                step: '2',
                title: 'Agent checks in automatically',
                desc: 'The agent on the broadcast computer polls the server every 60 seconds. When the schedule is ready, the agent downloads it silently — no action needed at the facility.',
              },
              {
                icon: Clock,
                color: 'text-orange-500',
                bg: 'bg-orange-500/10',
                step: '3',
                title: 'Stream starts on time',
                desc: 'At the scheduled start time, the agent sends a command to OBS to begin streaming. The broadcast goes live automatically — even if no one is in the room.',
              },
              {
                icon: Youtube,
                color: 'text-red-500',
                bg: 'bg-red-500/10',
                step: '4',
                title: 'YouTube title is updated',
                desc: 'About 30 seconds after the stream goes live, CSM renames the YouTube broadcast to include the team names, sheet, and event — so viewers immediately know what they\'re watching.',
              },
              {
                icon: Monitor,
                color: 'text-purple-500',
                bg: 'bg-purple-500/10',
                step: '5',
                title: 'Stream stops when the game ends',
                desc: 'At the scheduled end time, the agent tells OBS to stop streaming. The broadcast ends automatically, and the YouTube recording is saved.',
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 items-start">
                <div className={`h-10 w-10 rounded-lg ${item.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <item.icon className={`h-5 w-5 ${item.color}`} />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{item.title}</p>
                  <p className="text-muted-foreground text-sm mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <Card className="bg-muted/40 border-muted">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Hands-free from start to finish.</strong> Once the schedule is entered, the facility
                doesn't need to do anything. The agent handles OBS start, OBS stop, and YouTube title
                — even if the internet connection is briefly interrupted during the event.
              </p>
            </CardContent>
          </Card>

          <div className="mt-10">
            <h3 className="text-xl font-semibold mb-4">Ad hoc streams — no schedule needed</h3>
            <p className="text-muted-foreground mb-4">
              Sometimes a game comes up unexpectedly or you want to go live outside of the normal schedule.
              The on-site agent includes a built-in dashboard that lets you start a stream on the fly — no
              pre-entered schedule required.
            </p>
            <p className="text-muted-foreground mb-6">
              When starting an unscheduled stream, you enter the event name and team names at broadcast time.
              The agent still sets the YouTube broadcast title automatically using the same format and template
              as scheduled streams — so viewers see a properly titled broadcast regardless of how the stream
              was started.
            </p>
            <Card className="bg-muted/40 border-muted">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Agent dashboard only.</strong> Ad hoc streams are controlled
                  entirely through the agent's local dashboard — they don't create a schedule entry in CSM and
                  are not visible in the CSM schedule list. They're ideal for pick-up games, last-minute broadcasts,
                  or testing your OBS setup.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who manages what */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Who manages what?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Your Club Staff</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-muted-foreground">
                <p>✓ Enter and manage your facility's game schedule</p>
                <p>✓ Import schedules via CSV or sync from Google Sheets / CurlingClubManager</p>
                <p>✓ Pause, cancel, or reschedule individual streams</p>
                <p>✓ Set blackout dates when your facility won't be streaming</p>
                <p>✓ Start ad hoc streams through the agent dashboard (no schedule needed)</p>
                <p>✓ View live agent status and stream logs</p>
              </CardContent>
            </Card>
            <Card className="border-purple-200 dark:border-purple-800">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">CSM Administrator</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-muted-foreground">
                <p>✓ Install and configure the on-site agent (once per facility)</p>
                <p>✓ Connect YouTube channel (once per facility)</p>
                <p>✓ Set global blackout dates across all facilities</p>
                <p>✓ Monitor agent and stream status across all facilities</p>
                <p>✓ Manage schedules for any facility if needed</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-2xl mx-auto text-center space-y-4">
          <Button variant="outline" asChild>
            <Link to="/about/overlays">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Learn about Overlays &amp; Slideshows
            </Link>
          </Button>
          <span className="mx-4 text-muted-foreground">or</span>
          <Button asChild>
            <Link to="/login">
              Open Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
